import type { EngineDefinition } from './noise-suppression-engines';

/**
 * Machine-learning noise suppression for the microphone we send.
 *
 * The browser's own `noiseSuppression` is the WebRTC APM suppressor: it handles
 * stationary noise (fans, hum) and little else, which is why an open-plan office
 * still comes through. Teams and Meet sound cleaner because they run a learned
 * model instead. Windows has no OS-level equivalent we can lean on below
 * Copilot+ hardware, so we ship the model ourselves and run it in an
 * AudioWorklet — the approach Jitsi Meet has shipped for years.
 *
 * This processes the track we *send*, so the media servers, the far end and the
 * call recording all get the cleaned audio.
 *
 * Limitation — the app has to be running on the machine the headset is plugged
 * into. Under Citrix and other virtual desktops the app runs in a datacentre
 * while HDX RealTime Optimization routes the voice straight from the thin client
 * on the desk to the call servers, bypassing this graph entirely. Nothing here
 * can detect that: the setting reads as on, the log below says active, and the
 * model dutifully processes audio nobody is listening to. A customer reporting
 * "we turned it on and heard no difference" is the symptom to check for.
 */

export const NOISE_SUPPRESSION_ENGINES = ['off', 'rnnoise', 'gtcrn'] as const;

export type NoiseSuppressionEngine = (typeof NOISE_SUPPRESSION_ENGINES)[number];

/** Every engine except `off` — i.e. one that actually has a model behind it. */
export type ActiveNoiseSuppressionEngine = Exclude<
    NoiseSuppressionEngine,
    'off'
>;

/* GTCRN, on a listening comparison against the customer's own recording from
   PD-3288: it pulls the background down noticeably further than RNNoise while
   leaving the speaker's level untouched. RNNoise stays selectable as the cheap
   fallback for machines that cannot afford the stronger model. */
export const DEFAULT_NOISE_SUPPRESSION_ENGINE: ActiveNoiseSuppressionEngine =
    'gtcrn';

export const isNoiseSuppressionEngine = (
    value: unknown,
): value is NoiseSuppressionEngine =>
    NOISE_SUPPRESSION_ENGINES.includes(value as NoiseSuppressionEngine);

/**
 * Both models are built for 48 kHz, so the graph runs at that rate regardless of
 * what the capture device is doing — `createMediaStreamSource` resamples for us.
 */
const SUPPRESSION_SAMPLE_RATE = 48000;

export const isNoiseSuppressionSupported = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.AudioContext !== 'undefined' &&
    typeof window.AudioWorkletNode !== 'undefined' &&
    typeof WebAssembly !== 'undefined';

/**
 * The model binaries never change within a session, so fetch each one once and
 * share the buffer across calls. A failed load is evicted so the next call
 * retries rather than inheriting the rejection forever.
 */
const wasmBinaryCache = new Map<
    ActiveNoiseSuppressionEngine,
    Promise<ArrayBuffer>
>();

const loadEngine = async (
    engine: ActiveNoiseSuppressionEngine,
): Promise<EngineDefinition> => {
    const { ENGINE_DEFINITIONS } = await import('./noise-suppression-engines');
    return ENGINE_DEFINITIONS[engine];
};

const loadWasmBinary = (
    engine: ActiveNoiseSuppressionEngine,
    definition: EngineDefinition,
): Promise<ArrayBuffer> => {
    const cached = wasmBinaryCache.get(engine);
    if (cached !== undefined) {
        return cached;
    }

    const pending = definition.loadWasmBinary().catch((error: unknown) => {
        wasmBinaryCache.delete(engine);
        throw error;
    });
    wasmBinaryCache.set(engine, pending);
    return pending;
};

export type SuppressedMicrophone = {
    /** The denoised track to send in place of the raw microphone. */
    track: MediaStreamTrack;
    /** Tears down the graph. Does not touch `sourceTrack` — its owner stops it. */
    release: () => Promise<void>;
};

/**
 * Build `sourceTrack -> model -> destination` and hand back the destination's
 * track.
 *
 * The caller keeps ownership of `sourceTrack`: we only read from it, so whoever
 * opened the microphone (JsSIP, for a softphone call) still stops it.
 */
export const createNoiseSuppressedTrack = async (
    sourceTrack: MediaStreamTrack,
    engine: ActiveNoiseSuppressionEngine,
): Promise<SuppressedMicrophone> => {
    const definition = await loadEngine(engine);
    const wasmBinary = await loadWasmBinary(engine, definition);

    const context = new AudioContext({ sampleRate: SUPPRESSION_SAMPLE_RATE });

    try {
        await context.audioWorklet.addModule(definition.workletUrl);

        const source = context.createMediaStreamSource(
            new MediaStream([sourceTrack]),
        );
        const suppressor = definition.createNode(context, wasmBinary);
        const destination = context.createMediaStreamDestination();

        source.connect(suppressor).connect(destination);

        const [track] = destination.stream.getAudioTracks();
        if (!track) {
            throw new Error(
                'Noise suppression produced no output track to send',
            );
        }

        const release = async () => {
            suppressor.destroy();
            source.disconnect();
            suppressor.disconnect();
            track.stop();
            await context.close();
        };

        /* A suspended context renders silence into the destination track, while
           the caller still logs the model as active. Sticky activation makes
           this rare, but the failure mode is a silent call. */
        if (context.state === 'suspended') {
            await context.resume();
        }

        /* The handover point. The model runs now, so drop the browser's own
           suppressor: two suppressors in series is what produces the
           "underwater" artefacts. Nothing above this line changes the source
           track, so a model that failed to load leaves it as it was. Echo
           cancellation and gain control stay on. We do not replace those, and
           AEC in particular needs the render stream, which we cannot give it.
           Best effort: some drivers refuse to change this on a live track. */
        try {
            await sourceTrack.applyConstraints({ noiseSuppression: false });
        } catch {
            /* Keep going. A doubled suppressor still beats a failed call. */
        }

        return { track, release };
    } catch (error) {
        /* Never leave a context running behind a failed setup — an open
           AudioContext keeps the machine awake. */
        await context.close().catch(() => undefined);
        throw error;
    }
};
